import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  TextField,
  Select,
  Modal,
  Table,
  TableHeading,
  TableCell,
  Card,
  Button,
  Icon,
  IconButton,
  Tooltip,
  StatusIconProtoProps,
} from '@nike/eds';
import { useFormik } from 'formik';
import ManageAccess, { ManageAccessProps } from 'components/shared/manage-access/manage-access';
import styles from './family.module.css';
import { useI18nContext } from '../../context/language/use-i18n-context';
import ts from './i18n';
import { usePassPolicies } from 'context/PassPolicy';
import { Approvers } from 'components/shared/approvers/approvers';
import { Approver } from './type';
import NikeStoreTealIcon from 'assets/img/Nike-Store-Icon-Teal.png';
import ConverseTealIcon from 'assets/img/Converse-Star-Teal.png';
import NikeStoreGreyIcon from 'assets/img/Nike-Store-Icon-Grey.png';
import SwooshGreyIcon from 'assets/img/Swoosh-Icon-Grey.png';
import ConverseGreyIcon from 'assets/img/Converse-Star-Grey.png';
import SwooshTealIcon from 'assets/img/Swoosh-Icon-Teal.png';
import { ManageAccessModal } from 'models/IManageAccess';
import * as Yup from 'yup';
import { FamilyRelationship } from 'models/IRelationShips';
import { GuestPass } from '../../models/IPassRequest';
import { useUserContext } from '../../context/user';
import { InactivatePass, registerPass } from '../../services/pass-service';
import { HttpStatusCode } from 'axios';
import { FamilyPass } from '../../models/IFamilyPass';
import { AppConfig } from '../../utils/constants';
import { config } from 'process';

interface GridData {
  name: string;
  relationship: string;
  icons: { title: string; src: string }[];
  manageAccess: ManageAccessModal | undefined;
}

interface FamilyFormProps {
  isChinaRegion: boolean;
  isModalOpen: boolean;
  onDismiss: () => void;
  familyRelationships: FamilyRelationship[];
  setSnackbar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
  familyPassToEdit?: FamilyPass | null;
  activeFamilyCount: number;
}

interface ValidateResponse {
  storeChanged: boolean;
  swooshChanged: boolean;
  converseChanged: boolean;
}

const FamilyForm: React.FC<FamilyFormProps> = ({
  isModalOpen,
  isChinaRegion,
  onDismiss,
  familyRelationships,
  setSnackbar,
  familyPassToEdit,
  activeFamilyCount,
}) => {
  const { i18nString } = useI18nContext();
  const userInfo = useUserContext();
  const passPoliciesContext = usePassPolicies();
  const [updating, setUpdating] = useState<boolean>(false);
  const [passList, setPassList] = useState<GuestPass[]>([]);
  const [cancelList, setCancelList] = useState<number[]>([]);
  const [approver, setApprover] = useState<Approver>({
    id: '1', //managerId,
    name: '',
  });
  const familyDisclaimer =
    '入力されたご家族のメールアドレスに、NIKEより登録に関するメールが送付されます。ご家族本人より、本登録およびメールの送付について了承を得た上で入力してください';

  //   const [showApprover] = useState<boolean>(() => {
  //   const swooshPolicy =
  //     passPoliciesContext.passPolicies.filter(x => x.passTypeId === 105)[0] ?? {};
  //   const conversePolicy =
  //     passPoliciesContext.passPolicies.filter(x => x.passTypeId === 205)[0] ?? {};

  //   if ((swooshPolicy?.approvalFlag ?? 0) === 1 || (conversePolicy?.approvalFlag ?? 0) === 1) {
  //     //if swoosh or converse policy has approval flag, then show approver dropdown
  //     return true;
  //   }
  //   return false;
  // });

  const showApprover = useMemo(() => {
    return passPoliciesContext.passPolicies
      .filter(policy => {
        return (
          policy.passTypeToken === 'PASS_TYPE_FAMILY' ||
          policy.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY' ||
          policy.passTypeToken === 'PASS_TYPE_CONVERSE_FAMILY'
        );
      })
      ?.find(pol => pol?.approvalFlag === 1)
      ? true
      : false;
  }, []);

  const storePass = passPoliciesContext.passPolicies.find(
    x => x.passTypeToken === 'PASS_TYPE_FAMILY',
  );
  const swooshPass = passPoliciesContext.passPolicies.find(
    x => x.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY',
  );
  const conversePass = passPoliciesContext.passPolicies.find(
    x => x.passTypeToken === 'PASS_TYPE_CONVERSE_FAMILY',
  );

  const relationshipOptions = familyRelationships
    ? familyRelationships
        .filter(relationship => relationship.active)
        .map(relationship => ({
          value: relationship.kinId,
          label: i18nString(ts[relationship.relationshipToken as keyof typeof ts]),
        }))
    : [];

  const [validateResponse, setValidateResponse] = useState<ValidateResponse>({
    storeChanged: false,
    swooshChanged: false,
    converseChanged: false,
  });

  const [updatedManageAccessState, setUpdatedManageAccessState] = useState<ManageAccessModal>();
  const [disableUpdateButton, setDisableUpdateButton] = useState<boolean>(true);
  const passTypes = [
    {
      isActive: updatedManageAccessState?.passes.store.isActive,
      email: updatedManageAccessState?.passes.store.email,
      passTypeId: 5,
      passPolicyId: storePass?.passPolicyId ?? 0,
      shoppingDateEnd: storePass?.shoppingEndDate ?? new Date(),
      shoppingDateStart: storePass?.shoppingStartDate ?? new Date(),
    },
    {
      isActive: updatedManageAccessState?.passes.swoosh.isActive,
      email: updatedManageAccessState?.passes.swoosh.email,
      passTypeId: 105,
      passPolicyId: swooshPass?.passPolicyId ?? 0,
      shoppingDateEnd: swooshPass?.shoppingEndDate ?? new Date(),
      shoppingDateStart: swooshPass?.shoppingStartDate ?? new Date(),
    },
    {
      isActive: updatedManageAccessState?.passes.converse.isActive,
      email: updatedManageAccessState?.passes.converse.email,
      passTypeId: 205,
      passPolicyId: conversePass?.passPolicyId ?? 0,
      shoppingDateEnd: conversePass?.shoppingEndDate ?? new Date(),
      shoppingDateStart: conversePass?.shoppingStartDate ?? new Date(),
    },
  ];

  const handleManageAccessStateChange = (state: ManageAccessModal) => {
    setUpdatedManageAccessState(state);
    const { passes: currentPasses } = manageAccessState.manageAccess;
    const { passes: newPasses } = state;

    const validateResponse: ValidateResponse = {
      storeChanged: currentPasses.store.email !== newPasses.store.email,
      swooshChanged:
        currentPasses.swoosh.email !== newPasses.swoosh.email ||
        !currentPasses.swoosh.isActive === newPasses.swoosh.isActive,
      converseChanged:
        currentPasses.converse.email !== newPasses.converse.email ||
        currentPasses.converse.isActive !== newPasses.converse.isActive,
    };

    setValidateResponse(validateResponse);
    setDisableUpdateButton(!state.isValid);
  };

  const handleUpdate = async () => {
    setUpdating(true);

    if (!updatedManageAccessState) {
      return;
    }

    const passRequest = {
      approver:
        userInfo.passplayUser?.userCountry === 'JP'
          ? userInfo?.passplayUser.selfApprovalId
          : approver.id,
      guestPasses: passList,
    };
    try {
      if (passList.length > 0) {
        const response = await registerPass(userInfo?.passplayUser?.sponsorId, passRequest);
        if (response.status === HttpStatusCode.Ok) {
          const data = response.data;
          let SuccessCount = 0;
          let saveTxt;
          let saveTxtMultiple;

          if (familyPassToEdit && (familyPassToEdit?.access[0].passId ?? 0) > 0) {
            saveTxtMultiple = ts.FAMILY_UPDATED;
            saveTxt = ts.FAMILY_UPDATED;
          } else {
            saveTxtMultiple = ts.FAMILY_MULTIPLE_ADDED;
            saveTxt = ts.FAMILY_ADDED;
          }

          for (let i = 0; i < passList.length; i++) {
            if (isNaN(data[i])) {
              if (data[i].indexOf('unique') > -1) {
                setSnackbar({
                  showSnack: true,
                  message: i18nString(ts.FAMILY_ERROR_DUPLICATE),
                  snackStatus: 'success' as StatusIconProtoProps['status'],
                });
              } else {
                setSnackbar({
                  showSnack: true,
                  message: i18nString(ts.FAMILY_ERROR_ADDING_RECORD),
                  snackStatus: 'success' as StatusIconProtoProps['status'],
                });
              }
            } else {
              SuccessCount++;
            }
          }
          if (SuccessCount == passList.length) {
            if (SuccessCount > 1) {
              setSnackbar({
                showSnack: true,
                message: i18nString(saveTxtMultiple),
                snackStatus: 'success' as StatusIconProtoProps['status'],
              });
            } else {
              setSnackbar({
                showSnack: true,
                message: i18nString(saveTxt),
                snackStatus: 'success' as StatusIconProtoProps['status'],
              });
            }
          } else if (SuccessCount > 0) {
            setSnackbar({
              showSnack: true,
              message: i18nString(ts.FAMILY_ERROR_ADDING_RECORD),
              snackStatus: 'success' as StatusIconProtoProps['status'],
            });
          }
        } else {
          setSnackbar({
            showSnack: true,
            message: i18nString(ts.FAMILY_ERROR_ADDING_RECORD),
            snackStatus: 'error' as StatusIconProtoProps['status'],
          });
        }
      }
      if (cancelList.length > 0) {
        cancelList.forEach(async passId => {
          const cancelResponse = await InactivatePass(passId);
          if (cancelResponse.status === HttpStatusCode.Ok) {
            setSnackbar({
              showSnack: true,
              message: i18nString(ts.FAMILY_REMOVED),
              snackStatus: 'success' as StatusIconProtoProps['status'],
            });
          } else {
            setSnackbar({
              showSnack: true,
              message: 'An error occurred while inactivating the pass.',
              snackStatus: 'error' as StatusIconProtoProps['status'],
            });
          }
        });
      }
      onDismiss();
      setUpdating(false);
      setPassList([]);
      setCancelList([]);
    } catch (e) {
      onDismiss();
      setUpdating(false);
      setPassList([]);
      setCancelList([]);
    }
  };
  const isInActive =
    familyPassToEdit?.access?.find(access => access.passTypeToken === 'PASS_TYPE_FAMILY')
      ?.statusId === AppConfig.passStatus.registered;

  const [manageAccessState, setManageAccessState] = useState<ManageAccessProps>({
    manageAccess: {
      isChina: isChinaRegion,
      showSwoosh: !!swooshPass,
      showConverse: !!conversePass,
      isValid: true,
      passes: {
        store: {
          isActive: familyPassToEdit
            ? familyPassToEdit?.access?.find(access => access.passTypeToken === 'PASS_TYPE_FAMILY')
                ?.statusId === AppConfig.passStatus.registered
            : true,
          email:
            familyPassToEdit?.access?.find(access => access.passTypeToken === 'PASS_TYPE_FAMILY')
              ?.emailAddress ?? '',
          isDisabled: false,
          isEmailDisabled: !storePass || storePass?.emailFlag === 0,
        },
        swoosh: {
          isActive: familyPassToEdit
            ? familyPassToEdit?.access?.find(
                access => access.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY',
              )?.statusId === AppConfig.passStatus.registered
            : false,
          email:
            familyPassToEdit?.access?.find(
              access => access.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY',
            )?.emailAddress ?? '',
          isPendingApproval: false,
          isDisabled: !!familyPassToEdit?.passPlayIdRegistered,
          isEmailDisabled: !swooshPass || swooshPass?.emailFlag === 0,
        },
        converse: {
          isActive: familyPassToEdit
            ? familyPassToEdit?.access?.find(
                access => access.passTypeToken === 'PASS_TYPE_CONVERSE_FAMILY',
              )?.statusId === AppConfig.passStatus.registered
            : false,
          email:
            familyPassToEdit?.access?.find(
              access => access.passTypeToken === 'PASS_TYPE_CONVERSE_FAMILY',
            )?.emailAddress ?? '',
          isDisabled: false,
          isEmailDisabled: !conversePass || conversePass?.emailFlag === 0,
        },
      },
    },
    manageAccessStateChange: handleManageAccessStateChange,
  });

  const handleApproverChange = (option: any) => {
    setApprover({ id: option.value, name: option.label });
  };

  const columns = [
    { headerName: ts.GRID_HEADER_NAME.stringKey, field: 'name' },
    { headerName: ts.ACCESS_TEXT.stringKey, field: 'access' },
    {
      headerName: ts.GRID_HEADER_RELATIONSHIP.stringKey,
      field: 'relationship',
    },
    { headerName: ts.GRID_HEADER_ACTIONS.stringKey, field: 'action' },
  ];
  const [gridData, setGridData] = useState<GridData[]>([]);
  const [error, setError] = useState<string>();
  const disableAddButton = (): boolean => {
    return (
      disableUpdateButton ||
      !formik.isValid ||
      isFormInValid(formik.values) ||
      (isChinaRegion && activeFamilyCount + passList.length > 9)
    );
  };
  const formik = useFormik({
    initialValues: {
      firstName: familyPassToEdit?.firstName ?? '',
      lastName: familyPassToEdit?.lastName ?? '',
      relationshipId: familyPassToEdit?.relationshipId ?? null,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('Required field'),
      lastName: Yup.string().required('Required field'),
      relationshipId: Yup.number().required('Required field'),
    }),
    onSubmit: (values, { resetForm }) => {
      const newEntry: GridData = {
        name: `${values.firstName} ${values.lastName}`,
        relationship: relationshipOptions.find(x => x.value === values.relationshipId)?.label ?? '',
        icons: getIcons(),
        manageAccess: updatedManageAccessState,
      };
      setGridData([...gridData, newEntry]);
      addFamilyPass();
      handleClear();
      resetForm();
    },
  });
  //update family pass only if there is any change in existing family pass
  useEffect(() => {
    if (familyPassToEdit) {
      familyPassToEdit.access.forEach(access => {
        const { passTypeId, emailAddress, passId, statusId } = access;
        const pass = passTypes.find(pt => pt.passTypeId === passTypeId);

        // Check if the pass is changed and is valid and active
        let passChanged = false;
        if (passTypeId === 5 && validateResponse.storeChanged) passChanged = true;
        else if (passTypeId === 105 && validateResponse.swooshChanged) passChanged = true;
        else if (passTypeId === 205 && validateResponse.converseChanged) passChanged = true;
        else if (
          familyPassToEdit.firstName !== formik.values?.firstName ||
          familyPassToEdit.lastName !== formik.values?.lastName ||
          familyPassToEdit.relationshipId !== formik.values?.relationshipId
        ) {
          passChanged = true;
        }
        if (passChanged && updatedManageAccessState?.isValid && pass?.isActive && pass) {
          const newPass = addUpdatePass(
            pass?.email,
            passTypeId,
            pass.passPolicyId,
            pass.shoppingDateEnd,
            passId,
            familyPassToEdit?.visitorId ?? null,
            pass.shoppingDateStart,
          );
          setPassList(prevPassList => {
            // Replace if pass with same passTypeId exists, else add
            const existingIndex = prevPassList.findIndex(p => p.passTypeId === passTypeId);
            if (existingIndex !== -1) {
              const updatedList = [...prevPassList];
              updatedList[existingIndex] = newPass;
              return updatedList;
            }
            return [...prevPassList, newPass];
          });
        } else if (!pass?.isActive && passChanged && passId) {
          setCancelList(prevCancelList =>
            prevCancelList.includes(passId) ? prevCancelList : [...prevCancelList, passId],
          );
        }
      });
    }
  }, [updatedManageAccessState, formik.values]);
  const addUpdatePass = (
    email: string | undefined,
    passTypeId: number,
    passPolicyId: number,
    shoppingDateEnd: Date,
    passId: number | null,
    visitorId: number | null,
    shoppingDateStart: Date,
  ) => {
    const newPass: GuestPass = {
      firstName: formik.values.firstName,
      lastName: formik.values.lastName,
      emailAddress: email ?? '',
      relationshipId: formik.values.relationshipId,
      passTypeId,
      passId: passId,
      visitorId: visitorId,
      passLocationId: 0,
      passPolicyId,
      shoppingDateStart,
      shoppingDateEnd,
      sponsorId: userInfo?.passplayUser?.sponsorId ?? 0,
    };
    return newPass;
  };
  const addFamilyPass = () => {
    passTypes
      .filter(x => x.isActive)
      .forEach(({ email, passTypeId, passPolicyId, shoppingDateEnd, shoppingDateStart }) => {
        const newPass = addUpdatePass(
          email,
          passTypeId,
          passPolicyId,
          shoppingDateEnd,
          null,
          null,
          shoppingDateStart,
        );
        setPassList(prevPassList => [...prevPassList, newPass]);
      });
  };
  const handleEdit = (index: number) => {
    const entry = gridData[index];
    const [firstName, lastName] = entry.name.split(' ');

    formik.setValues({
      firstName,
      lastName,
      relationshipId: relationshipOptions.find(x => x.label === entry.relationship)?.value ?? null,
    });

    if (entry.manageAccess) {
      setManageAccessState({
        manageAccess: entry.manageAccess,
        manageAccessStateChange: handleManageAccessStateChange,
      });
    }
    setPassList(prevPassList =>
      prevPassList.filter(pass => !(pass.firstName === firstName && pass.lastName === lastName)),
    );
    setGridData(gridData.filter((_, i) => i !== index));
  };

  const handleDelete = (index: number) => {
    setGridData(gridData.filter((_, i) => i !== index));
    setPassList(prevPassList =>
      prevPassList.filter(pass => !(pass.firstName + ' ' + pass.lastName === gridData[index].name)),
    );
  };

  const handleClear = () => {
    formik.resetForm();

    // Reset the ManageAccess state to its initial state
    setManageAccessState({
      manageAccess: {
        isChina: isChinaRegion,
        showSwoosh: !!swooshPass,
        showConverse: !!conversePass,
        isValid: true,
        passes: {
          store: {
            isActive: true,
            email: '',
            isDisabled: false,
            isEmailDisabled: !storePass || storePass?.emailFlag === 0,
          },
          swoosh: {
            isActive: false,
            email: '',
            isPendingApproval: false,
            isDisabled: false,
            isEmailDisabled: !swooshPass || swooshPass?.emailFlag === 0,
          },
          converse: {
            isActive: false,
            email: '',
            isDisabled: false,
            isEmailDisabled: !conversePass || conversePass?.emailFlag === 0,
          },
        },
      },
      manageAccessStateChange: handleManageAccessStateChange,
    });
  };

  const getIcons = () => {
    const icons: { title: string; src: string }[] = [];

    if (storePass) {
      if (updatedManageAccessState?.passes.store.isActive) {
        icons.push({ title: 'Store', src: NikeStoreTealIcon });
      } else {
        icons.push({ title: 'Store', src: NikeStoreGreyIcon });
      }
    }

    if (conversePass) {
      if (updatedManageAccessState?.passes.converse.isActive) {
        icons.push({ title: 'Converse', src: ConverseTealIcon });
      } else {
        icons.push({ title: 'Converse', src: ConverseGreyIcon });
      }
    }

    if (swooshPass) {
      if (updatedManageAccessState?.passes.swoosh.isActive) {
        icons.push({ title: 'Swoosh', src: SwooshTealIcon });
      } else {
        icons.push({ title: 'Swoosh', src: SwooshGreyIcon });
      }
    }

    return icons;
  };
  const isFormEmpty = (values: any): boolean => {
    return values.relationshipId == null && values.firstName == '' && values.lastName == '';
  };
  const isFormInValid = (values: any): boolean => {
    return values.relationshipId == 0 || values.firstName == '' || values.lastName == '';
  };

  const isSwooshLinked = !!familyPassToEdit?.passPlayIdRegistered;
  const isSwooshActive =
    familyPassToEdit?.access?.find(access => access.passTypeToken === 'PASS_TYPE_SWOOSH_FAMILY')
      ?.statusId === AppConfig.passStatus.registered;

  const getSwooshToolTipMessage = () => {
    const field = isChinaRegion ? 'mobile number' : 'email';
    return (
      <span className={styles.swoosh_message_text}>
        Need to update your registered family Swoosh account {field}? Visit{' '}
        <span className={styles.swoosh_message_link}>account settings</span> on your family Swoosh
        account to update.
      </span>
    );
  };

  return (
    <Modal
      headerSlot={
        <>
          <center>
            {familyPassToEdit !== null && <h1>{i18nString(ts.FAMILY_MODAL_EDIT_FAMILY)}</h1>}
            {familyPassToEdit === null && <h1>{i18nString(ts.FAMILY_MODAL_ADD_FAMILY)}</h1>}
          </center>
        </>
      }
      isOpen={isModalOpen}
      onDismiss={onDismiss}
      className={styles.pass__modal_container}
      hideFade
    >
      {showApprover && <Approvers onApproverChange={handleApproverChange} />}
      <section className="eds-background--white eds-spacing--p-20 section-border eds-spacing--mt-16">
        <form onSubmit={formik.handleSubmit}>
          <div className="eds-spacing--mt-12 eds-spacing--mb-12">
            <h4 className={styles.manage_access_header}>
              {i18nString(ts.FAMILY_MODAL_FAMILY_INFO_HEADING)}
            </h4>
          </div>
          <div className="eds-flex eds-flex--direction-row eds-flex--wrap eds-flex--align-content-flex-start eds-flex--justify-content-space-between eds-spacing--mb-16">
            <div className={`column`}>
              <TextField
                id="firstName"
                label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                aria-label={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_FIRST_NAME)}
                type="text"
                name="firstName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
                data-testid="first_name"
                hasErrors={formik.touched.firstName && !!formik.errors.firstName}
                errorMessage={formik.touched.firstName ? formik.errors.firstName : ''}
              />
            </div>
            <div className={`column ${styles.form_input}`}>
              <TextField
                id="lastName"
                label={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                aria-label={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                hideLabel={true}
                placeholder={i18nString(ts.PLACEHOLDER_LAST_NAME)}
                type="text"
                name="lastName"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                data-testid="last_name"
                hasErrors={formik.touched.lastName && !!formik.errors.lastName}
                errorMessage={formik.touched.lastName ? formik.errors.lastName : ''}
              />
            </div>
          </div>
          <div className="eds-flex eds-flex--wrap eds-flex--justify-content-space-between eds-spacing--mb-16">
            <div className={styles.half_width}>
              <Select
                id="relationshipId"
                label={i18nString(ts.DDL_RELATIONSHIP_SELECT)}
                aria-label={i18nString(ts.DDL_RELATIONSHIP_SELECT)}
                hideLabel={true}
                data-testid="relationship-select"
                options={relationshipOptions}
                placeholder={i18nString(ts.DDL_RELATIONSHIP_SELECT)}
                value={
                  relationshipOptions.find(
                    option => option.value === formik.values.relationshipId,
                  ) || null
                }
                onChange={(option: any) => formik.setFieldValue('relationshipId', option.value)}
                onBlur={formik.handleBlur}
                hasErrors={formik.touched.relationshipId && !!formik.errors.relationshipId}
                errorMessage={formik.touched.relationshipId ? formik.errors.relationshipId : ''}
              />
            </div>
          </div>
        </form>
        <ManageAccess
          manageAccess={manageAccessState.manageAccess}
          manageAccessStateChange={manageAccessState.manageAccessStateChange}
        />
        {familyPassToEdit && isSwooshLinked && isSwooshActive && (
          <div className="eds-flex eds-flex--justify-content-center eds-spacing--mt-12">
            <div className={styles.swoosh_message_container}>
              <span className={styles.swoosh_message_icon}>
                <Icon name="InfoCircleFilled" color="#62e5ae" />
              </span>
              {getSwooshToolTipMessage()}
            </div>
          </div>
        )}
        {(userInfo.passplayUser?.userCountry === 'JP' ||
          userInfo.passplayUser?.userCountry === 'Japan') && (
          <div className="eds-flex eds-flex--direction-column eds-flex--noWrap">
            {familyDisclaimer}
          </div>
        )}
        {familyPassToEdit === null && (
          <div className={`eds-flex eds-flex--justify-content-flex-end ${styles.div_width}`}>
            <Tooltip isDark={true} placement={'bottom'} bodySlot={'Clear'}>
              <IconButton
                icon="Close"
                className="eds-spacing--mr-8"
                onClick={handleClear}
                label="Clear"
                data-testid="clear"
                aria-label="clear"
                disabled={isFormEmpty(formik.values)}
              />
            </Tooltip>
            <Tooltip isDark={true} placement={'bottom'} bodySlot={'Add'}>
              <IconButton
                icon="Plus"
                onClick={(e: any) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
                label="Add"
                data-testid="Add"
                className="eds-spacing--m-2"
                disabled={disableAddButton()}
              />
            </Tooltip>
          </div>
        )}
      </section>
      {familyPassToEdit === null && (
        <div className="eds-flex eds-flex--justify-content-center eds-flex--align-items-flex-center">
          <Table className="eds-table" width="100%">
            <thead>
              <tr key="accessAccountHeader">
                {columns.map(column => (
                  <TableHeading
                    className={styles.table_header}
                    data-testid={column.headerName}
                    key={column.headerName}
                  >
                    <span className={`table_header_style`}>
                      {i18nString(ts[column.headerName as keyof typeof ts])}
                    </span>
                  </TableHeading>
                ))}
              </tr>
            </thead>
            <tbody>
              {gridData.map((visitor, index) => (
                <tr key={index}>
                  <TableCell className={styles.table_td}>{visitor.name}</TableCell>
                  <TableCell className={styles.table_td}>
                    {visitor.icons.map((item, index) => (
                      <img
                        key={index}
                        title={item.title}
                        src={item.src}
                        alt="icon"
                        className={styles.grid_image}
                      />
                    ))}
                  </TableCell>
                  <TableCell className={styles.table_td}>{visitor.relationship}</TableCell>
                  <TableCell key={index}>
                    <IconButton
                      label="edit pass"
                      aria-label="edit pass"
                      size="small"
                      disabled={false}
                      onClick={() => handleEdit(index)}
                      icon={'Edit'}
                      variant="ghost"
                    ></IconButton>
                    <IconButton
                      label="delete pass"
                      aria-label="delete pass"
                      size="small"
                      disabled={false}
                      onClick={() => handleDelete(index)}
                      icon={'Close'}
                      variant="ghost"
                      data-testid="delete-pass"
                    ></IconButton>
                  </TableCell>
                </tr>
              ))}

              {(gridData.length === 0 || error !== undefined) && (
                <tr>
                  <TableCell className={styles.table_td} colSpan={4}>
                    {error
                      ? error
                      : i18nString(ts.FAMILY_MODAL_NO_FAMILY_ADDED_TEXT).replace(/\\/g, '"')}
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
      <div className="eds-flex eds-flex--justify-content-center eds-spacing--m-48">
        {!updating && familyPassToEdit === null && (
          <Button
            type="submit"
            className="txt_uppercase"
            aria-label="saveFamilyPassButton"
            variant="primary"
            data-testid="register"
            onClick={handleUpdate}
            disabled={passList.length === 0}
          >
            {i18nString(ts.FAMILY_MODAL_REGISTER)}
          </Button>
        )}
        {!updating && familyPassToEdit && (
          <Button
            type="submit"
            className="txt_uppercase"
            aria-label="updateFamilyPassButton"
            variant="primary"
            data-testid="update"
            onClick={handleUpdate}
            disabled={
              disableUpdateButton ||
              (passList.length === 0 && cancelList.length === 0 && !familyPassToEdit) ||
              (disableAddButton() && !!familyPassToEdit)
            }
          >
            {i18nString(ts.FAMILY_MODAL_UPDATE)}
          </Button>
        )}
        {updating && <span>{i18nString(ts.SAVING_TEXT)}</span>}
      </div>
    </Modal>
  );
};

export default FamilyForm;
